import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";

const MIN_VISIBLE_MS = 120;
const FADE_OUT_MS = 400;
// Hard cap — even if a query hangs or an image is broken, never trap the user.
const MAX_VISIBLE_MS = 2200;
// Race fonts.ready against this — fonts shouldn't block the overlay.
const FONTS_WAIT_MS = 250;
// After this much DOM-quiet time with no new in-viewport images, declare ready.
const DOM_QUIET_MS = 140;
// Poll React Query idle state every this often.
const QUERY_POLL_MS = 40;

const isImageReady = (img: HTMLImageElement) =>
  img.complete && img.naturalWidth > 0;

const waitForImage = (img: HTMLImageElement): Promise<void> => {
  if (isImageReady(img)) {
    return img.decode ? img.decode().catch(() => undefined) : Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const done = () => {
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      if (img.decode) {
        img.decode().then(() => resolve(), () => resolve());
      } else {
        resolve();
      }
    };
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
};

const collectVisibleImages = (): HTMLImageElement[] => {
  const root = document.querySelector("main") ?? document.body;
  if (!root) return [];
  const viewportH = window.innerHeight || 800;
  const viewportW = window.innerWidth || 1280;
  const imgs = Array.from(root.querySelectorAll("img")) as HTMLImageElement[];
  return imgs.filter((img) => {
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    if (rect.bottom <= 0 || rect.top >= viewportH) return false;
    if (rect.right <= 0 || rect.left >= viewportW) return false;
    if (!img.currentSrc && !img.src) return false;
    if (img.loading === "lazy" && rect.top > viewportH * 0.9) return false;
    return true;
  });
};

/** Find an explicit LCP image anywhere in the DOM (data-lcp="true"). */
const findLcpImage = (): HTMLImageElement | null =>
  document.querySelector<HTMLImageElement>('img[data-lcp="true"]');

const PageTransitionOverlay = () => {
  const location = useLocation();
  const [phase, setPhase] = useState<"visible" | "fading" | "hidden">("visible");
  const showTimeRef = useRef(Date.now());
  const prevPathRef = useRef(location.pathname);
  const isInitialRef = useRef(true);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const minTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const waitTokenRef = useRef(0);
  const isFetching = useIsFetching();
  const isFetchingRef = useRef(isFetching);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);

  const hide = useCallback(() => {
    const elapsed = Date.now() - showTimeRef.current;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);

    clearTimeout(minTimerRef.current);
    minTimerRef.current = setTimeout(() => {
      setPhase("fading");
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = setTimeout(() => setPhase("hidden"), FADE_OUT_MS);
    }, remaining);
  }, []);

  // Wait for: (1) React Query idle, (2) fonts, (3) explicit LCP image,
  // (4) all in-viewport images decoded, with a DOM-mutation-driven quiet window.
  const waitForReady = useCallback(() => {
    const token = ++waitTokenRef.current;
    const startedAt = Date.now();
    let hidden = false;

    const safeHide = () => {
      if (hidden) return;
      if (token !== waitTokenRef.current) return;
      hidden = true;
      hide();
    };

    const maxTimer = setTimeout(safeHide, MAX_VISIBLE_MS);

    const trackedAwaits = new Set<HTMLImageElement>();
    let lastDiscoveredAt = Date.now();

    const scanAndWait = async (): Promise<boolean> => {
      const imgs = collectVisibleImages();
      const fresh = imgs.filter((img) => !trackedAwaits.has(img));
      if (fresh.length === 0) return false;
      fresh.forEach((img) => trackedAwaits.add(img));
      lastDiscoveredAt = Date.now();
      await Promise.all(fresh.map((img) => waitForImage(img)));
      return true;
    };

    const run = async () => {
      await new Promise<void>((r) =>
        requestAnimationFrame(() => requestAnimationFrame(() => r()))
      );

      // 1) Wait for React Query to become idle (data for the new route arrived).
      //    Polling is cheap and avoids subscribing to QueryCache events here.
      while (
        isFetchingRef.current > 0 &&
        Date.now() - startedAt < MAX_VISIBLE_MS
      ) {
        if (token !== waitTokenRef.current) return;
        await new Promise((r) => setTimeout(r, QUERY_POLL_MS));
      }

      // 2) Fonts (raced against a cap).
      if (document.fonts?.ready) {
        await Promise.race([
          document.fonts.ready.catch(() => undefined),
          new Promise((r) => setTimeout(r, FONTS_WAIT_MS)),
        ]);
      }

      // 3) Wait briefly for the LCP image to mount and decode, if the page
      //    declares one. Poll up to ~600 ms past data-ready time.
      const lcpDeadline = Date.now() + 600;
      while (Date.now() < lcpDeadline) {
        if (token !== waitTokenRef.current) return;
        const lcp = findLcpImage();
        if (lcp) {
          trackedAwaits.add(lcp);
          await waitForImage(lcp);
          break;
        }
        await new Promise((r) => setTimeout(r, 40));
      }

      // 4) Discovery loop: watch DOM mutations + decode every in-viewport image
      //    as it appears, until the DOM stays quiet (no new viewport images)
      //    for DOM_QUIET_MS. This catches images that mount after route data.
      const observer = new MutationObserver(() => {
        // Schedule a scan; if it discovers anything, lastDiscoveredAt updates.
        void scanAndWait();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Initial scan after data is ready.
      await scanAndWait();

      // Quiet-window loop.
      while (Date.now() - startedAt < MAX_VISIBLE_MS) {
        if (token !== waitTokenRef.current) {
          observer.disconnect();
          return;
        }
        await scanAndWait();
        if (Date.now() - lastDiscoveredAt >= DOM_QUIET_MS) break;
        await new Promise((r) => setTimeout(r, 40));
      }
      observer.disconnect();

      clearTimeout(maxTimer);
      safeHide();
    };

    run();
  }, [hide]);

  // Initial load
  useEffect(() => {
    if (!isInitialRef.current) return;
    isInitialRef.current = false;

    const start = () => waitForReady();
    if (document.readyState === "complete") {
      start();
    } else {
      window.addEventListener("load", start, { once: true });
      return () => window.removeEventListener("load", start);
    }
  }, [waitForReady]);

  // Route changes
  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;

    clearTimeout(fadeTimerRef.current);
    clearTimeout(minTimerRef.current);
    showTimeRef.current = Date.now();
    setPhase("visible");

    waitForReady();
  }, [location.pathname, waitForReady]);

  if (phase === "hidden") return null;

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        width: "100vw",
        height: "100vh",
        background: "linear-gradient(180deg, #0B0B0C 0%, #121214 100%)",
        pointerEvents: phase === "fading" ? "none" : "all",
        opacity: phase === "fading" ? 0 : 1,
        transition:
          phase === "fading"
            ? `opacity ${FADE_OUT_MS}ms cubic-bezier(0.16, 1, 0.30, 1)`
            : "none",
      }}
    >
      <span
        className="font-sans uppercase select-none text-[42px] sm:text-[56px] md:text-[64px] lg:text-[72px] tracking-[0.35em] font-medium"
        style={{
          color: "#A8A9AD",
          animation:
            reducedMotion || phase === "fading"
              ? "none"
              : "motai-pulse 2000ms ease-in-out infinite",
        }}
      >
        MOTAI
      </span>
    </div>
  );
};

export default PageTransitionOverlay;
